import React, { useState, useEffect, useMemo } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";
import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const TrainingDetailsSales = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalSales, setTotalSales] = useState(0);
  const [salesPerProduct, setSalesPerProduct] = useState([]); 
  const [salesPerHub, setSalesPerHub] = useState([]); 
  const [salesPerUser, setSalesPerUser] = useState([]);

  const [viewType, setViewType] = useState("product");
  const [chartType, setChartType] = useState("pie"); 

  const [selectedProduct, setSelectedProduct] = useState("all");
  const [selectedHub, setSelectedHub] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");

  const { sendRequest: fetchData } = useHttp();

  useEffect(() => {
    const orgId = localStorage.getItem("orgId") || "";
    const url = `/api/trainingDetailSalesAgriPromoter/?orgId=${orgId}`;

    setLoading(true);
    fetchData({ url }, (res) => {
      setTotalSales(res?.totalSales || 0);
      setSalesPerProduct(res?.salesPerProduct || []);
      setSalesPerHub(res?.salesPerHub || []);
      setSalesPerUser(res?.salesPerUser || []);
      setLoading(false);
    });
  }, [fetchData]);

  const productOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (salesPerProduct || []).map((p) => p.productName).filter(Boolean)
        )
      ),
    [salesPerProduct]
  );

  const hubOptions = useMemo(
    () =>
      (salesPerHub || []).map((h) => ({
        id: h.hubId,
        name: h.hubName || `Hub ${h.hubId}`,
      })),
    [salesPerHub]
  );

  const userOptions = useMemo(
    () =>
      (salesPerUser || []).map((u) => ({
        id: u.userId,
        name: u.userName || `User ${u.userId}`,
      })),
    [salesPerUser]
  );

  const chartDataset = useMemo(() => {
    if (viewType === "product") {
      if (selectedProduct === "all") {
        return (salesPerProduct || []).map((p) => ({
          productName: p.productName ?? "Unknown",
          totalSales: p.totalSales ?? 0,
          hubName: p.hubName ?? null,
        }));
      }
      const hubs = (salesPerHub || [])
        .map((h) => {
          const prod = (h.products || []).find(
            (pr) => pr.productName === selectedProduct
          );
          return prod
            ? {
                hubId: h.hubId,
                hubName: h.hubName || `Hub ${h.hubId}`,
                totalSales: prod.totalSales || 0,
              }
            : null;
        })
        .filter(Boolean);
      if (hubs.length) return hubs;
      const users = (salesPerUser || [])
        .map((u) => {
          const prod = (u.products || []).find(
            (pr) => pr.productName === selectedProduct
          );
          return prod
            ? {
                userId: u.userId,
                userName: u.userName || `User ${u.userId}`,
                totalSales: prod.totalSales || 0,
              }
            : null;
        })
        .filter(Boolean);
      return users;
    }

    if (viewType === "hub") {
      if (selectedHub === "all") {
        return (salesPerHub || []).map((h) => ({
          hubId: h.hubId,
          hubName: h.hubName ?? `Hub ${h.hubId}`,
          totalSales: h.totalSales ?? 0,
          products: h.products || [],
        }));
      }
      const hub = (salesPerHub || []).find(
        (h) => String(h.hubId) === String(selectedHub)
      );
      if (!hub) return [];
      return (hub.products || []).map((p) => ({
        productName: p.productName ?? "Unknown",
        totalSales: p.totalSales ?? 0,
      }));
    }

    if (selectedUser === "all") {
      return (salesPerUser || []).map((u) => ({
        userId: u.userId,
        userName: u.userName ?? `User ${u.userId}`,
        totalSales: u.totalSales ?? 0,
        products: u.products || [],
      }));
    }
    const user = (salesPerUser || []).find(
      (u) => String(u.userId) === String(selectedUser)
    );
    if (!user) return [];
    return (user.products || []).map((p) => ({
      productName: p.productName ?? "Unknown",
      totalSales: p.totalSales ?? 0,
    }));
  }, [
    viewType,
    selectedProduct,
    selectedHub,
    selectedUser,
    salesPerProduct,
    salesPerHub,
    salesPerUser,
  ]);

  const displayValue = useMemo(() => {
    if (viewType === "product") {
      if (selectedProduct === "all") return totalSales;
      return chartDataset.reduce((s, r) => s + (r.totalSales || 0), 0);
    }
    if (viewType === "hub") {
      if (selectedHub === "all") return totalSales;
      return chartDataset.reduce((s, r) => s + (r.totalSales || 0), 0);
    }
    // user
    if (selectedUser === "all") return totalSales;
    return chartDataset.reduce((s, r) => s + (r.totalSales || 0), 0);
  }, [
    viewType,
    selectedProduct,
    selectedHub,
    selectedUser,
    totalSales,
    chartDataset,
  ]);

  const noData =
    !loading &&
    !salesPerProduct.length &&
    !salesPerHub.length &&
    !salesPerUser.length;

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: 20,
          background: "#f1f7ff",
          borderRadius: 12,
          boxSizing: "border-box",
        }}
      >
        <div
          className="flex items-center mb-4"
          style={{ gap: 10, flexWrap: "wrap" }}
        >
          <Select
            value={viewType}
            style={{ width: 150, marginRight: 5 }}
            onChange={(v) => {
              setViewType(v);
              setSelectedProduct("all");
              setSelectedHub("all");
              setSelectedUser("all");
            }}
          >
            <Option value="product">Product Wise</Option>
            <Option value="hub">Hub Wise</Option>
          </Select>

          {viewType === "product" && (
            <Select
              value={selectedProduct}
              style={{ width: 180, marginRight: 5 }}
              onChange={(v) => {
                setSelectedProduct(v);
                setSelectedHub("all");
                setSelectedUser("all");
              }}
            >
              <Option value="all">All Products</Option>
              {productOptions.map((p) => (
                <Option key={p} value={p}>
                  {p}
                </Option>
              ))}
            </Select>
          )}

          {viewType === "hub" && (
            <Select
              value={selectedHub}
              style={{ width: 180, marginRight: 5 }}
              onChange={(v) => {
                setSelectedHub(v);
                setSelectedProduct("all");
              }}
            >
              <Option value="all">All Hubs</Option>
              {hubOptions.map((h) => (
                <Option key={h.id} value={h.id}>
                  {h.name}
                </Option>
              ))}
            </Select>
          )}

          <Select
            value={chartType}
            style={{ width: 150, marginLeft: "auto" }}
            onChange={(v) => setChartType(v)}
          >
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        {loading ? (
          <div
            style={{
              height: "calc(100% - 80px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
          </div>
        ) : noData ? (
          <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
            <h3 style={{ marginBottom: 4 }}>No sales data</h3>
          </div>
        ) : (
          <>
            <div
              style={{
                textAlign: "center",
                marginBottom: 16,
                padding: 12,
                background: "#e0ecff",
                borderRadius: 10,
              }}
            >
              <h1
                style={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: "#123e73",
                  marginBottom: 4,
                }}
              >
                ₹ {Number(displayValue || 0).toLocaleString()}
              </h1>
              <p style={{ fontSize: 16, color: "#123e73", margin: 0 }}>
                {viewType === "product"
                  ? selectedProduct === "all"
                    ? "Total Sales (All Products)"
                    : `Sales — ${selectedProduct}`
                  : viewType === "hub"
                  ? selectedHub === "all"
                    ? "Total Sales (All Hubs)"
                    : `Sales — ${
                        hubOptions.find(
                          (h) => String(h.id) === String(selectedHub)
                        )?.name || "Selected Hub"
                      }`
                  : selectedUser === "all"
                  ? "Total Sales (All Users)"
                  : `Sales — ${
                      userOptions.find(
                        (u) => String(u.id) === String(selectedUser)
                      )?.name || "Selected User"
                    }`}
              </p>
            </div>

            <div style={{ height: "100%", overflow: "hidden" }}>
              {chartType === "pie" && (
                <DynamicPieChart
                  data={chartDataset}
                  angleField="totalSales"
                  colorField={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "productName"
                        : chartDataset[0]?.hubName
                        ? "hubName"
                        : "userName"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "hubName"
                        : "productName"
                      : selectedUser === "all"
                      ? "userName"
                      : "productName"
                  }
                  isMaximized={true}
                />
              )}

              {chartType === "column" && (
                <DynamicColumnChart
                  data={chartDataset}
                  xField={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "productName"
                        : chartDataset[0]?.hubName
                        ? "hubName"
                        : "userName"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "hubName"
                        : "productName"
                      : selectedUser === "all"
                      ? "userName"
                      : "productName"
                  }
                  yField="totalSales"
                  seriesField={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "productName"
                        : chartDataset[0]?.hubName
                        ? "hubName"
                        : "userName"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "hubName"
                        : "productName"
                      : selectedUser === "all"
                      ? "userName"
                      : "productName"
                  }
                  xAxisTitle={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "Product"
                        : "Hub/User"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "Hub"
                        : "Product"
                      : selectedUser === "all"
                      ? "User"
                      : "Product"
                  }
                  yAxisTitle="Sales Amount"
                />
              )}

              {chartType === "bar" && (
                <DynamicBarChart
                  data={chartDataset}
                  xField={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "productName"
                        : chartDataset[0]?.hubName
                        ? "hubName"
                        : "userName"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "hubName"
                        : "productName"
                      : selectedUser === "all"
                      ? "userName"
                      : "productName"
                  }
                  yField="totalSales"
                  seriesField={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "productName"
                        : chartDataset[0]?.hubName
                        ? "hubName"
                        : "userName"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "hubName"
                        : "productName"
                      : selectedUser === "all"
                      ? "userName"
                      : "productName"
                  }
                  xAxisTitle={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "Product"
                        : "Hub/User"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "Hub"
                        : "Product"
                      : selectedUser === "all"
                      ? "User"
                      : "Product"
                  }
                  yAxisTitle="Sales Amount"
                />
              )}

              {chartType === "line" && (
                <DynamicLineChart
                  data={chartDataset}
                  xField={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "productName"
                        : chartDataset[0]?.hubName
                        ? "hubName"
                        : "userName"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "hubName"
                        : "productName"
                      : selectedUser === "all"
                      ? "userName"
                      : "productName"
                  }
                  yField="totalSales"
                  seriesField={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "productName"
                        : chartDataset[0]?.hubName
                        ? "hubName"
                        : "userName"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "hubName"
                        : "productName"
                      : selectedUser === "all"
                      ? "userName"
                      : "productName"
                  }
                  xAxisTitle={
                    viewType === "product"
                      ? selectedProduct === "all"
                        ? "Product"
                        : "Hub/User"
                      : viewType === "hub"
                      ? selectedHub === "all"
                        ? "Hub"
                        : "Product"
                      : selectedUser === "all"
                      ? "User"
                      : "Product"
                  }
                  yAxisTitle="Sales Amount"
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: 200,
        width: "100%",
        padding: "18px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : noData ? (
        <div
          className="flex justify-center items-center h-full"
          style={{ color: "#3d5c26" }}
        >
          <div style={{ textAlign: "center" }}>
            <h3 style={{ margin: 0 }}>No Training Deatils Sales</h3>
            <p style={{ marginTop: 4, fontSize: 13 }}>No records found</p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1 style={{ fontWeight: 700, color: "#3d5c26", marginBottom: 4 }}>
              {Number(totalSales || 0).toLocaleString()}
            </h1>
            <h3 style={{ fontWeight: 700, margin: 0, color: "#3d5c26" }}>
              Training Details Sales
            </h3>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "8px",
              right: "18px",
              cursor: "pointer",
              fontWeight: "600",
              color: "#3d5c26",
              fontSize: "14px",
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default TrainingDetailsSales;
